import { Configure, Hits, useConnector, useSortBy } from 'react-instantsearch-hooks-web';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect } from 'react';
import type {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import { useSession } from 'next-auth/react';
import { CustomPagination } from '../../CustomPagination';
import classNames from 'classnames';
import { HitProps } from '../../Hits/_types';
import { FilterBuilder } from 'lib/util/filter-builder';
import NrcSearchHit from 'components/helpers/AlgoliaSearch/NetworkResourceCenter/NRCSearchHit';
import { DEFAULT_SEARCH_INDEX } from 'lib/util/algolia';
import { distinctAnnouncementAudiencesForCenterTypes } from 'lib/util/audiences';
import { Session } from 'next-auth';
import fnNoOp from 'lib/util/no-op';

export type UseStatsProps = StatsConnectorParams;

type UseStatsWithSessionProps = UseStatsProps & {
  session: Session;
};

function useStats(props?: UseStatsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, props);
}

const AnnouncementListingSearchHit = ({ hit }: HitProps) => {
  return <NrcSearchHit hit={hit} component="announcementListing" />;
};

const buildAnnouncementFilterString = (centerTypes: string[]): string => {
  const filterBuilder = new FilterBuilder();
  distinctAnnouncementAudiencesForCenterTypes(centerTypes).forEach((centerType: string) =>
    filterBuilder.appendFilter(`audience: "${centerType}"`)
  );
  const centerFilter = filterBuilder.toString();
  return centerFilter.length > 0
    ? `type: announcement AND (${centerFilter})`
    : `type: announcement`;
};

const SearchResultsWithSearch = (props: UseStatsWithSessionProps) => {
  const { refine, canRefine } = useSortBy({
    items: [
      { label: 'sortDate', value: `${DEFAULT_SEARCH_INDEX}_date_desc` },
      { label: 'default', value: `${DEFAULT_SEARCH_INDEX}` },
    ],
  });
  const { session } = props;
  useEffect(() => {
    const isBrowser = typeof window !== 'undefined'; // Check if window is defined
    if (isBrowser && canRefine) {
      const delay = 100;
      const timer = setTimeout(() => {
        refine(`${DEFAULT_SEARCH_INDEX}_date_desc`);
      }, delay);
      return () => clearTimeout(timer);
    }
    return fnNoOp;
  }, [refine, canRefine]);

  const { nbHits } = useStats(props);
  const configureFilters = buildAnnouncementFilterString(session?.center_types || []);
  const showPagination = nbHits > 10;
  return (
    <div>
      <Configure analytics={false} filters={configureFilters} hitsPerPage={10} />

      <div>
        <Hits hitComponent={AnnouncementListingSearchHit} />
      </div>
      <div className={classNames({ hidden: !showPagination })}>
        <CustomPagination padding={1} />
      </div>
    </div>
  );
};

const SearchResults = (props: UseStatsProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const { data: session } = useSession();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return <div>Search Results will be shown here</div>;
  }
  if (!session) {
    return <></>;
  }
  return <SearchResultsWithSearch session={session} {...props} />;
};

export default SearchResults;
